import { useEffect, useRef } from 'react';
import {
  Briefcase, CalendarHeart, CheckCheck, Clock, FileSpreadsheet, LayoutDashboard,
  MapPin, Package, ShieldCheck, TrendingUp, Users,
} from 'lucide-react';
import { SectionHeader } from '../SectionHeader';
import { Reveal } from '../Reveal';
import { trackEvent } from '@/lib/analytics';

const FEATURE_ICONS = {
  'Management Dashboard': LayoutDashboard,
  'Approvals': CheckCheck,
  'Timesheets': Clock,
  'Employees': Users,
  'Leave and Sickness': CalendarHeart,
  'Compliance': ShieldCheck,
  'Customers and Sites': MapPin,
  'Materials and Costs': Package,
  'Site Profitability': TrendingUp,
  'Customer Profitability': Briefcase,
  'Payroll Preparation': FileSpreadsheet,
};

// Section 4 — core platform capabilities.
export const PulseCapabilities = ({ sp }) => {
  const ref = useRef(null);
  const seen = useRef(false);

  useEffect(() => {
    if (!ref.current || seen.current) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !seen.current) {
          seen.current = true;
          trackEvent('pulse_feature_viewed');
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} aria-labelledby="pulse-capabilities-heading" data-testid="pulse-capabilities" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="04"
          eyebrow="Platform Capabilities"
          title="One connected view of your workforce and operations"
          description="Pulse connects workforce information with operational and commercial visibility — far more than a basic timesheet app or holiday-request tool."
          testid="pulse-capabilities-header"
        />
        <ul className="mt-14 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
          {(sp.features || []).map((feature, i) => {
            const Icon = FEATURE_ICONS[feature.title] || Clock;
            return (
              <Reveal key={feature.title} delay={(i % 3) * 0.06}>
                <li className="flex items-start gap-4" data-testid={`pulse-feature-${i + 1}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy-900 text-gold-500">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-base font-extrabold tracking-tight text-navy-900">{feature.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{feature.text}</p>
                  </div>
                </li>
              </Reveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
